// Authentication + client area copy — English (reference language).
const app = {
  common: {
    edit: 'edit',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    create: 'Create',
    close: 'Close',
    delete: 'Delete',
    loading: 'Loading…',
    working: '…',
    back: 'Back',
    backHome: 'Back to home',
    dash: '—',
    yes: 'Yes',
    no: 'No'
  },

  field: {
    show: 'Show',
    hide: 'Hide',
    showPassword: 'Show password',
    hidePassword: 'Hide password'
  },

  errors: {
    email: 'Enter a valid email address.',
    orderEmail: 'Enter the email address from your order.',
    password: 'Enter your password.',
    passwordLength: 'At least 8 characters.',
    passwordMismatch: 'The passwords do not match.',
    emailTaken: 'That address is already in use.',
    currentPassword: 'Enter your current password.',
    network: "Can't reach the server. Check that Apache is running.",
    http: status => `Error ${status}`,
    inactiveSub: 'Subscription inactive.'
  },

  login: {
    title: 'Good to see you again.',
    subtitle: 'Log in to find your lock, your schedules and your day counter.',
    quote: 'Every day held counts double.',
    caption: 'Your space',
    justSubscribed: 'Just subscribed?',
    claimLink: 'First login',
    noAccount: 'No account yet?',
    plansLink: 'See the plans',
    emailLabel: 'Email address',
    emailPlaceholder: 'you@example.com',
    passLabel: 'Password',
    passPlaceholder: 'Your password',
    forgot: 'Forgot your password?',
    // Pas de réinitialisation automatique : la demande part sur WhatsApp.
    forgotMsg: e => `Hi, I forgot my SELFCNTRL password.${e ? ' My email: ' + e : ''}`,
    submit: 'Log in',
    submitting: 'Logging in…',
    stuck: 'Can’t get in?',
    stuckLink: 'Message us on WhatsApp'
  },

  register: {
    // Page d'inscription « caisse » (9 oct. 2026).
    steps: ['Account', 'Payment', 'Lock a device'],
    eyebrow: 'Step 1 of 3 · about 1 minute',
    pageTitle: 'Create your space.',
    pageSub: 'Pick a plan, choose a password. Payment is next, on Stripe.',
    perDayMonthly: '≈ 33¢ a day',
    perDayYearly: '≈ 14¢ a day',
    sumTitle: 'Your order',
    sumIncl: 'What you get',
    sumItems: ['Every device you own, locked', 'Can’t be removed at 2am', 'Night curfews & extra blocking', 'Cashback every month you hold', 'Human support on WhatsApp'],
    sumToday: 'Due today',
    sumThen: p => p === 'yearly' ? 'Then $49.99 every year. Cancel anytime.' : 'Then $9.99 every month. Cancel anytime.',
    sumGuarT: '30-day money-back',
    sumGuarP: 'Full refund, no questions. And $100 on top if you manage to get around the lock.',
    nextT: 'What happens next',
    next: ['Secure payment on Stripe', 'A 10-minute guided setup', 'Your day counter starts'],
    secure: 'Encrypted · Payment by Stripe · Discreet billing',
    terms: 'By continuing you accept the',
    termsLink: 'terms',
    // Plus d'essai depuis le 26/09/2026 : les deux formules se paient tout de
    // suite, le titre ne promet donc rien de gratuit.
    title: 'Lock it tonight.',
    titleYear: 'Lock in the whole year.',
    subtitle: <>1 minute to create your account,<br />5 minutes to set up your protection.</>,
    quote: 'What you leave behind.',
    caption: 'Day 0',
    haveAccount: 'Already have an account?',
    loginLink: 'Log in',
    // Le montant réellement débité aujourd'hui, et ce qui rentre ensuite.
    // Plus d'essai depuis le 26/09/2026 : la carte est débitée à l'inscription.
    freeT: 'Total today: $9.99',
    freeS: '30-day money-back — and get paid for holding',
    // Shown instead of the two above when the yearly plan is selected.
    yearT: 'Total today: $49.99',
    yearS: '$4.17 a month, billed yearly — 30-day money-back',
    planGroup: 'Plan',
    monthly: 'Monthly',
    monthlyPrice: '$9.99',
    monthlyPer: '/ month',
    yearly: 'Yearly',
    yearlyPrice: '$49.99',
    yearlyPer: '/ year',
    // Struck reference price on the yearly plan: the REAL cost of twelve
    // months on the monthly plan ($9.99 × 12), never an invented price.
    yearlyWas: '$120/yr',
    // « Best value » badge on the yearly plan: we steer toward the year
    // (less churn, and aligned with a self-control product where the monthly
    // « cancel » button is itself a temptation).
    best: 'Best value',
    emailLabel: 'Email address',
    emailPlaceholder: 'you@example.com',
    passLabel: 'Password',
    passPlaceholder: '8 characters minimum',
    submit: 'Get started',
    submitYear: 'Get the year',
    submitting: 'One moment…',
    // Reassurance bar under the button: the REAL guarantees, at the exact
    // moment of choice. The « $100 bet » (beat the block → refund + $100) is
    // our differentiator; it lived in the FAQ, never under the button.
    // Nothing invented here — each item already exists in the terms / refund
    // policy.
    // Reassurance block UNDER the button (like the competitor): the last word
    // goes to the guarantees, not to a friction point. Nothing invented —
    // each line already exists in the terms / refund policy.
    pay: 'Secure payment',
    // ⚠️ Only show Apple Pay / Google Pay if they are actually enabled in the
    // Stripe dashboard: a wallet promised here but missing on the payment page
    // causes drop-off. Cards are always accepted.
    brands: ['Apple Pay · Google Pay', 'Visa', 'Mastercard', 'Amex'],
    guar: [
      '30-day refund, no questions asked. Cancel in one click.',
      'Zero-access guarantee: find a way around the block? Refund, plus $100.',
      'SSL payment via Stripe. Your card never touches our servers. Discreet.'
    ],
    guarYear: [
      '30-day refund, no questions asked, on request.',
      'Zero-access guarantee: find a way around the block? Refund, plus $100.',
      'SSL payment via Stripe. Your card never touches our servers. Discreet.'
    ],
    // Social proof at the top of the form: credibility before the offer.
    // Figures PROVIDED by the operator — do not inflate them here without an
    // update from them; unverifiable social proof costs more than it earns.
    stats: [
      { n: '10 min', l: 'To lock a device' },
      { n: '$100', l: 'If you beat it' },
      { n: '30 days', l: 'Money-back' }
    ],
    // The debit / refund detail is now carried by the guarantees under the
    // button; only the acceptance of the terms remains here.
    note: 'By continuing you accept the ',
    noteLife: 'By continuing you accept the ',
    noteLink: 'terms and conditions',
    fallbackName: 'me'
  },

  claim: {
    title: 'Activate your space.',
    subtitle: "You've just subscribed. Choose your password and your account is ready.",
    quote: 'The door is waiting for you.',
    caption: 'First login',
    havePassword: 'Already have a password?',
    loginLink: 'Log in',
    emailLabel: 'Order email',
    emailPlaceholder: 'you@example.com',
    emailHint: 'The one you used at checkout.',
    passLabel: 'Choose a password',
    passPlaceholder: 'At least 8 characters',
    confirmLabel: 'Confirm the password',
    confirmPlaceholder: 'Type it again',
    submit: 'Activate my space',
    submitting: 'Activating…'
  },

  // Contact dans le tableau de bord : plus de formulaire depuis le
  // 26/09/2026, un bouton vers notre WhatsApp Business.
  contactForm: {
    title: 'Write to us',
    lead: 'On WhatsApp. We answer in under an hour, between 9am and 11pm.',
    waCta: 'Message us on WhatsApp',
    v2: { live: 'A real person answers', waS: 'The fastest way. Your account is attached to the message, no need to explain who you are.' },
    waMsg: email => `Hi, I need help with SELFCNTRL.${email ? ` Account: ${email}` : ''}`,
    navLabel: 'Contact'
  },

  welcome: {
    title: "Here we go.",
    subtitle: 'Your three free days start now. One thing left to do.',
    quote: 'Day one.',
    caption: 'The counter starts',
    problem: 'Something wrong?',
    write: 'Write to us',
    noPayment: `No payment detected. If you just paid, wait a few seconds and
                reload the page.`,
    seePlans: 'See the plans',
    payment: 'Payment',
    recorded: 'Recorded',
    lock: 'Lock',
    lockReady: 'Ready to set',
    lockActivating: 'Activating…',
    nextReady: 'Next step: lock your devices. Count on ten minutes, once.',
    nextWaiting: "We're waiting for payment confirmation. It takes a few seconds.",
    cta: 'Lock my devices',

    // Accueil d'un nouveau client. Le bouton principal envoie au TUTORIEL et
    // non au verrou : quelqu'un qui débarque sur la page « Mon verrou » sans
    // avoir vu la marche à suivre ne sait pas qu'il lui faut un câble et son
    // ordinateur, et il repart. Le verrou reste accessible juste en dessous.
    helloT: 'Welcome among us.',
    helloP: `You're in. Take three minutes with the walkthrough first — it shows
             every screen you'll see, in the order you'll see them. It's the
             difference between being locked tonight and giving up at step two.`,
    helloMeta: '3 parts · 19 steps · real screenshots',
    ctaTutorial: 'Open the walkthrough',
    ctaSkip: 'Skip, go straight to my lock'
  },

  // ==========================================================================
  //  ONBOARDING — le funnel post-paiement.
  //
  //  Son seul but : amener le nouveau client à VERROUILLER un premier
  //  appareil, et ancrer la décision. On ne le re-vend pas (il a payé) : on
  //  enlève le remords, on lui montre exactement quoi faire selon ce qu'il
  //  veut protéger, puis on lui tend une main s'il bloque. « Passer à la
  //  configuration » reste accessible à chaque étape.
  // ==========================================================================
  onboarding: {
    skip: 'Skip to setup',
    back: 'Back',
    next: 'Continue',
    // La légende sous l'illustration, sur toutes les étapes.
    art: { k: 'Day 0', line: 'The counter starts the moment the lock is on.' },
    stepOf: (i, n) => `Step ${i} of ${n}`,
    rail: { welcome: 'Welcome', device: 'Your device', computer: 'Your computer', need: 'What you need', video: 'Follow along', help: 'Done', soon: 'A computer', more: 'Your setup' },

    // 1 — Accueil. On enlève le remords, on pose l'attente.
    welcome: {
      badgeOk: 'Payment confirmed',
      badgeWait: 'Confirming payment…',
      badgeFail: 'Payment not confirmed yet',
      title: 'You just did the hardest part.',
      body: `Deciding was the hard bit — and it's done. What's left is ten
             quiet minutes of setup. The first five days ask the most; after
             that the urge fades to background noise, then to nothing.`,
      trialLine: n => n != null
        ? `Your trial is running — ${n} free day${n > 1 ? 's' : ''} left.`
        : 'Your access is active.',
      waitLine: `We're still confirming your payment — it usually takes a few
                 seconds. You can keep going, your setup won't wait on it.`,
      failLine: `We haven't received your payment yet. You can go through the
                 setup below, but your lock won't arm until it clears.`,
      cta: "Let's set it up",
      perksK: 'Optional · free CNTRL',
      // Le prénom / pseudo : c'est ce qu'on affiche partout ensuite (« Hi X »).
      nameLabel: 'What should we call you?',
      namePh: 'First name or nickname',
      nameHint: 'No real name needed. This is how the app greets you.',
      // Opt-in WhatsApp incentivé (canal de relance direct). Le crédit CNTRL
      // se fait à la main pour l'instant (le message pré-rempli porte le compte).
      wa: {
        reward: '+100 CNTRL',
        title: 'Join us on WhatsApp',
        body: 'A direct line if you get stuck. 100 CNTRL for saying hi.',
        cta: 'Join on WhatsApp',
        msg: "Hi! I'd like to join on WhatsApp and claim my 100 CNTRL 🎁",
        done: '100 CNTRL added ✓ — say hi so we can help.',
        already: 'Already in your account — say hi anytime.'
      },
      dc: {
        reward: '+100 CNTRL',
        title: 'Join the community',
        body: 'People doing the same thing as you, awake at the same hours. 100 CNTRL for showing up.',
        cta: 'Join the Discord',
        done: '100 CNTRL added ✓ — see you in #introductions.',
        already: 'Already in your account — the server is always open.'
      }
    },

    // 2 — Quoi protéger. Pilote tout le reste du funnel.
    device: {
      title: 'What do you want to protect first?',
      lead: `Start with one. You can lock the others right after — same ten
             minutes each.`,
      opts: {
        iphone: { t: 'iPhone or iPad', s: 'The reinforced, un-removable lock' },
        windows: { t: 'Windows PC', s: 'Filtering that survives a reinstall' },
        mac: { t: 'Mac', s: 'DNS filtering — download the app' }
      },
      note: 'More than one? Pick the one that matters most tonight.'
    },

    // 2b — Pour l'iOS : quel ordinateur, car la recette passe par un PC.
    computer: {
      kicker: 'One more question',
      title: 'What computer can you use?',
      lead: `Locking an iPhone or iPad runs once through a computer and a
             cable. It's what makes the lock impossible to peel off later.`,
      opts: {
        windows: { t: 'A Windows PC', s: 'Ready now — full walkthrough inside' },
        mac: { t: 'A Mac', s: 'Ready now — full walkthrough inside' },
        none: { t: "I don't have", s: 'You\'ll need one for the iPhone lock' }
      },
      noneNote: `Borrow one for ten minutes — a friend's, a family PC. The
                 cable and the computer are only needed once, to seal the lock.`
    },

    // La réponse précédente, rappelée au-dessus de la question suivante.
    picked: { chose: 'You chose', change: 'Change' },

    // 2c — Cas « aucun ordinateur » : l'iPhone a besoin d'un ordi une fois.
    // (Le Mac, lui, a maintenant sa propre étape de téléchargement.)
    soon: {
      title: "You'll need a computer for this one.",
      body: `Locking an iPhone or iPad runs once through a computer and a USB
             cable — Windows or Mac. Borrow one for ten minutes and you're set.
             Want the setup link by email for when you have it?`,
      done: "Sent — it'll be in your inbox when you're ready.",
      notify: 'Email me the setup link',
      placeholder: 'you@email.com',
      send: 'Send it',
      sending: 'Saving…',
      err: 'That address looks off — check it?',
      other: 'Protect something else instead',
      helpBtn: 'Need a hand? Message us',
      meanwhile: `Got a Windows PC or a Mac nearby after all? Go back and pick
                  it — both are live now.`
    },

    // 3 — Prérequis, selon le chemin.
    need: {
      title: "Here's what you'll need.",
      lead: 'Gather these and you\'re ten minutes from a locked device.',
      minutes: '~10 minutes, once',
      lists: {
        iosWin: [
          'Your iPhone or iPad',
          'A Windows PC',
          'A USB cable (the one you charge with)'
        ],
        windows: [
          'This Windows PC',
          'Two minutes to install and seal the filter'
        ],
        mac: [
          'This Mac',
          'Two minutes to install and approve the filter'
        ],
        iosMac: [
          'Your iPhone or iPad',
          'Your Mac',
          'A USB cable (the one you charge with)'
        ]
      },
      ready: 'I have everything — show me how',
      later: "I'll do it later"
    },

    // 4 — La vidéo.
    video: {
      title: 'Watch it once, then do it.',
      lead: `The whole thing, screen by screen. Follow along on your device —
             pause whenever you need.`,
      caps: {
        iosWin: 'Locking an iPhone from a Windows PC',
        windows: 'Filtering a Windows PC',
        mac: 'Protecting a Mac',
        iosMac: 'Supervising an iPhone from a Mac'
      },
      fullT: 'Prefer written steps?',
      fullL: 'Read them right here — no need to leave the page.',
      fullShots: 'See the same steps with screenshots →',
      // Étapes écrites, affichées à droite de la vidéo. Une entrée par chemin
      // technique — elles doublent la vidéo pour qui préfère lire ou suivre à
      // son rythme.
      steps: {
        iosWin: [
          { t: 'Open the Supervisor app.',
            p: `Download and launch SELFCNTRL Supervisor on your PC. Have the
                cable, iTunes (from apple.com) open, and your Apple password
                ready.` },
          { t: 'Turn off Stolen Device Protection.',
            p: `On the iPhone: Settings → Face ID & Passcode → Stolen Device
                Protection → off. Skip if the phone has no passcode. It's
                temporary — you put it back at the end.` },
          { t: 'Turn off Find My.',
            p: `Settings → your name → Find My → Find My iPhone → off, entering
                your Apple password. Apple refuses supervision otherwise. It's
                only for the next few minutes: you turn it back on at the end,
                and it works exactly as before.` },
          { t: 'Plug in the iPhone.',
            p: `Connect it by USB and tap "Trust". When its name and iOS version
                show in the app, the button lights up.` },
          { t: 'Start supervision.',
            p: `Click "Supervise now" and don't unplug. The iPhone restarts once
                — that's normal, nothing is erased.` },
          { t: 'Put the protections back.',
            p: `Turn Find My and Stolen Device Protection back on. Sign in to
                your Apple account again if asked.` },
          { t: 'Get the code and attach.',
            p: `On the site, open "My lock" → "Lock", copy the eight-character
                code into the app, and confirm.` },
          { t: "Install the filter — it's locked.",
            p: `In the same window, open the "2 · Filter" tab and scan the QR
                with the iPhone camera. iOS says "Not verified" — install anyway.
                The device then shows "Locked".` }
        ],
        windows: [
          { t: 'Open "My Lock" on the site.',
            p: `In your dashboard, open "My Lock" and keep this tab open — you'll
                copy a pairing code from here near the end.` },
          { t: 'Download SELFCNTRL.',
            p: `On "My Lock", open the Windows tab and download the app — a
                small .zip file.` },
          { t: 'Extract the folder first.',
            p: `Don't run it straight from the .zip: right-click it, choose
                "Extract all", and confirm the destination.` },
          { t: 'Open SELFCNTRL.exe.',
            p: `In the extracted folder, double-click SELFCNTRL.exe. Windows may
                warn it's from an unknown publisher — choose "More info", then
                "Run anyway".` },
          { t: 'Choose "Protect this computer".',
            p: `The app opens and asks for a code. The field is empty — that's
                where the pairing code goes.` },
          { t: 'Copy your pairing code.',
            p: `Back on the site, on "My Lock", copy the eight-character code.` },
          { t: 'Paste it, then protect.',
            p: `Paste the code into the app and click "Protect". It installs the
                filter and its protection service by itself — nothing else to
                click.` },
          { t: "It's locked.",
            p: `The computer shows as protected, and the service restarts on
                every boot — so the filter holds even through a reinstall
                attempt.` }
        ],
        mac: [
          { t: 'Open "My Lock" on the site.',
            p: `In your dashboard, open "My Lock" and keep this tab open — you'll
                copy a pairing code from here near the end.` },
          { t: 'Download SELFCNTRL for Mac.',
            p: `On "My Lock", open the Mac tab and download the build for your
                chip (Apple Silicon or Intel).` },
          { t: 'Open SELFCNTRL.app.',
            p: `Extract the .tar.gz, then double-click SELFCNTRL.app. It's signed
                by Apple, so macOS just asks once ("downloaded from the internet")
                — click "Open".` },
          { t: 'Choose "Protect this Mac".',
            p: `The app opens and asks for a code. The field is empty — that's
                where the pairing code goes.` },
          { t: 'Copy your pairing code.',
            p: `Back on the site, on "My Lock", copy the eight-character code.` },
          { t: 'Paste it, then protect.',
            p: `Paste the code into the app and click "Protect this Mac".` },
          { t: 'Approve the profile.',
            p: `macOS opens the profile: System Settings → General → VPN & Device
                Management → approve "SELFCNTRL". Removal needs your removal
                password.` },
          { t: "It's locked.",
            p: `The Mac shows as protected and the filter reloads on every boot.` }
        ],
        iosMac: [
          { t: 'Open SELFCNTRL for Mac.',
            p: `On "My Lock" open the Mac tab, download the build for your chip,
                extract the .tar.gz and open SELFCNTRL.app. No iTunes needed on a
                Mac. Have your Apple password ready.` },
          { t: 'Turn off Stolen Device Protection.',
            p: `On the iPhone: Settings → Face ID & Passcode → Stolen Device
                Protection → off. Skip if the phone has no passcode. It's
                temporary — you put it back at the end.` },
          { t: 'Turn off Find My.',
            p: `Settings → your name → Find My → Find My iPhone → off, entering
                your Apple password. Apple refuses supervision otherwise. It's
                only for the next few minutes: you turn it back on at the end,
                and it works exactly as before.` },
          { t: 'Plug the iPhone into the Mac.',
            p: `Connect it by USB and tap "Trust". When its name shows in the app,
                the button lights up.` },
          { t: 'Start supervision.',
            p: `Choose "Supervise an iPhone" and don't unplug. The iPhone restarts
                once — that's normal, nothing is erased.` },
          { t: 'Put the protections back.',
            p: `Turn Find My and Stolen Device Protection back on. Sign in to your
                Apple account again if asked.` },
          { t: 'Get the code and attach.',
            p: `On the site, open "My lock" → "Lock", copy the eight-character
                code into the app, and confirm.` },
          { t: "Install the filter — it's locked.",
            p: `In the same window, open the "2 · Filter" tab and scan the QR with
                the iPhone camera. iOS says "Not verified" — install anyway. The
                device then shows "Locked".` }
        ]
      },
      cta: "Done watching — next"
    },

    // 5 — Ancrage. Un engagement, pas un réglage backend.
    anchor: {
      title: 'Lock in your decision.',
      lead: `The people who make it past week one are the ones who set the
             terms up front — not on a tired evening. Choose yours now.`,
      paceLabel: 'How you cut',
      paces: {
        straight: {
          t: 'Straight cut',
          s: 'Seven days a week, from minute one. Hardest for five days, by far the shortest.'
        },
        gradual: {
          t: 'Gradual taper',
          s: 'Four locked days to start, then one more every two weeks. Seven out of seven in six weeks.'
        }
      },
      whyLabel: 'Why you\'re doing this',
      whyHint: `One line, for you alone. Nobody else sees it — you will, on the
                hard nights.`,
      whyPlaceholder: 'e.g. To be present for the people I love.',
      floorNote: 'Whatever you pick, it only ever tightens — never loosens on a whim.',
      cta: 'Commit and continue'
    },

    // 6 — Aide + sortie vers la config.
    help: {
      title: "You're all set.",
      doneKicker: 'Done',
      welcomeT: name => name ? `Welcome aboard, ${name}.` : 'Welcome aboard.',
      welcomeLead: 'That was the hard part. From here it is ten quiet minutes, then the lock does the rest.',
      lead: 'Everything is ready — arm your first device now. It only takes about ten minutes.',
      stuck: 'Stuck during setup? Message us on WhatsApp',
      channels: {
        whatsapp: 'Message us on WhatsApp',
        telegram: 'Message us on Telegram',
        discord: 'Join us on Discord'
      },
      email: 'Email us',
      emailFallback: 'Or email us',
      anon: 'No real name needed — a first name or a nickname is plenty.',
      finishT: 'Ready to lock it in?',
      ctaConfig: 'Go to setup',
      ctaReactivate: 'Activate my subscription',
      reactivateNote: 'Your payment hasn\'t cleared — activate to arm the lock.',
      ctaDownload: 'Download the app'
    }
  },

  // ==========================================================================
  //  ACTIVATION — bandeau persistant + checklist de mise en route.
  //
  //  Le bandeau suit le client sur CHAQUE page du tableau de bord tant que
  //  quelque chose bloque : paiement à récupérer, ou verrou pas encore posé.
  //  C'est la réponse directe aux comptes qui paient avec une carte morte
  //  puis disparaissent — on ne les laisse pas oublier.
  // ==========================================================================
  activation: {
    // Paiement jamais abouti (checkout abandonné, aucune formule).
    noneT: 'Your setup isn\'t finished.',
    noneS: 'Pick up where you left off — ten minutes to a locked device.',
    noneCta: 'Finish setup',
    // Paiement échoué / abonnement retombé (carte refusée, fonds insuffisants).
    dueT: 'Your payment didn\'t go through.',
    dueS: 'Your lock is off until it clears. Update your card to turn it back on.',
    dueCta: 'Fix my payment',
    // Payé mais aucun appareil verrouillé — le vrai moment de valeur manque.
    armT: 'Almost there — nothing is locked yet.',
    armS: 'Your access is live. Lock your first device to actually shut the door.',
    armCta: 'Lock a device',

    // Checklist de mise en route, sur la vue d'ensemble.
    checkT: 'Get to your first locked device',
    checkS: n => `${n} of 3 done`,
    checkDone: 'You\'re fully set up. The rest is just holding.',
    steps: {
      account: { t: 'Account created', s: 'Done — you\'re in.' },
      pay: { t: 'Subscription active', s: 'Payment confirmed.', sOff: 'Payment not confirmed yet.' },
      lock: { t: 'First device locked', s: 'At least one device protected.', sOff: 'Nothing is locked yet — this is the one that counts.' }
    },
    resume: 'Open the setup guide'
  },

  // Coque de l'app (refonte du 9 oct. 2026).
  shell: {
    tabs: { overview: 'Today', protection: 'Lock', sos: 'SOS', rank: 'Progress', rewards: 'Rewards', menu: 'Menu' },
    more: 'Profile & settings',
    sections: { main: 'Main', more: 'More' },
    hello: (n, h) => `${h < 5 ? 'Still up' : h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'}${n ? ', ' + n : ''}`,
    sosCta: 'Urge SOS',
    sosSub: 'One tap, the minute it hits',
    groups: { progress: 'Progress', help: 'Help', account: 'Account' }
  },

  dash: {
    nav: {
      overview: 'Overview',
      protection: 'My lock',
      tutorial: 'Tutorial',
      rank: 'My rank',
      goals: 'Goals',
      rewards: 'Rewards',
      subscription: 'Subscription',
      account: 'My account',
      contact: 'Contact us',
      sos: 'Urge SOS'
    },
    fallbackTitle: 'My space',
    logout: 'Log out',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    dayShort: 'd',
    more: 'More',
    sub: {
      sos: 'The minute it hits',
      rewards: 'Your CNTRL and gift cards',
      tutorial: 'Set up the lock in ten minutes',
      goals: 'Where the time you get back goes',
      subscription: 'Plan, billing, invoices',
      account: 'Name, password, time zone',
      contact: 'A real answer within 24 h'
    },
    pending: n => `${n} device${n > 1 ? 's' : ''} to lock`
  },

  status: {
    labels: {
      trialing: 'Trial in progress',
      active: 'Subscription active',
      past_due: 'Payment pending',
      canceled: 'Subscription cancelled',
      none: 'No subscription'
    },
    inactiveNote: 'Filtering is suspended while the subscription is inactive.',
    seePlans: 'See the plans',
    noneTitle: 'No device locked',
    noneTrial: n => `Your subscription is running (${n} trial day${n > 1 ? 's' : ''} left), but nothing is protected yet. Count on ten minutes.`,
    noneNote: 'Your subscription is running, but nothing is protected yet. Count on ten minutes.',
    lockDevice: 'Lock a device',
    onTitle: 'Lock active',
    onCount: n => `${n} device${n > 1 ? 's' : ''} locked`,
    onPending: n => ` · ${n} still pending`,
    onDone: '. There is no open door left.'
  },

  rankBand: {
    kicker: 'Your rank',
    days: n => <><b>{n}</b> day{n > 1 ? 's' : ''} held</>,
    toward: name => `Toward ${name}`,
    left: n => `${n} day${n > 1 ? 's' : ''} to go`,
    seeLadder: 'See the ladder'
  },

  rankSwiper: {
    day: n => n === 0 ? 'Day 0' : `Day ${n}`,
    reached: 'Reached',
    now: 'Your rank',
    inDays: n => `In ${n} day${n > 1 ? 's' : ''}`,
    locked: 'Keep holding to unlock it.',
    prev: 'Previous rank',
    next: 'Next rank'
  },

  ranks: {
    dechu: { name: 'Fallen', line: 'The first day is the hardest. You have started it.' },
    eveille: { name: 'Awakened', line: 'Your brain protests. That is the sign it is working.' },
    novice: { name: 'Novice', line: 'One week. Sleep is starting to come back.' },
    gardien: { name: 'Guardian', line: 'Two weeks. The urge is rarer and weaker.' },
    sentinelle: { name: 'Sentinel', line: 'One month. People around you notice without knowing why.' },
    seraphin: { name: 'Seraph', line: 'Two months. It is no longer a fight, it is your normal life.' },
    archange: { name: 'Archangel', line: 'Four months. You no longer count the days the same way.' },
    elu: { name: 'Chosen', line: 'One year. You have become someone else.' }
  },

  overview: {
    // Tant que rien n'est verrouillé, l'accueil s'ouvre sur CETTE carte :
    // le produit n'existe pas avant le premier appareil verrouillé.
    setup: {
      kicker: 'First thing to do',
      title: 'Lock your first device.',
      lead: 'Your subscription is running, but nothing is protected yet. Count on ten minutes — that is the whole product.',
      steps: ['Pick the device: iPhone, iPad, Mac or Windows', 'Follow the video, one step at a time', 'Scan the code — the lock is on'],
      cta: 'Start the tutorial',
      alt: 'I already have the app',
      note: 'Your day counter starts the moment the first device is locked.'
    },
    daysHeld: n => `day${n > 1 ? 's' : ''} held`,
    since: d => `Since ${d}`,
    profiles: n => `Profile${n > 1 ? 's' : ''}`,
    devices: n => `Device${n > 1 ? 's' : ''}`,
    supervised: n => `Supervised`,
    coverage: 'Coverage',
    hello: name => `Hi ${name}`,
    // 45 minutes a day is a conservative average for heavy use.
    reclaimed: h => `about ${h} hour${h > 1 ? 's' : ''} reclaimed`,
    thisWeek: 'This week',
    weekHint: n => n >= 7 ? 'Seven for seven.' : `${n} of the last 7 days held`,
    quick: 'Quick actions',
    lockedDevices: 'Devices locked',
    stillOpen: n => `${n} device${n > 1 ? 's' : ''} still open — `,
    lockIt: 'lock it',
    blocked: {
      title: 'Requests blocked',
      sub: n => `over the last ${n} days`,
      lead: 'On your filtered devices. This counts everything blocked — including ads, trackers and scripts that call these sites on their own, without anyone going there.',
      empty: 'No request blocked yet — as soon as a filtered device hits forbidden content, it counts here.',
      unavailable: 'Cloudflare logs are momentarily unavailable.',
      catsT: 'What was blocked',
      cats: {
        adult: 'Adult content',
        gambling: 'Gambling',
        dating: 'Dating',
        social: 'Social networks',
        gaming: 'Games',
        other: 'Other'
      },
      today: 'today',
      // When it hits: the same blocked requests, by hour of the day.
      hoursT: 'When it hits',
      hoursLead: (a, b) => `Most of it lands between ${a} and ${b}. That is the window to cut.`,
      hoursFlat: 'Spread across the day — no single window stands out yet.',
      hoursCta: 'Cut the internet at night'
    }
  },



  calendar: {
    prev: 'Previous month',
    next: 'Next month',
    held: n => n === 0 ? 'Nothing held this month yet' : `${n} day${n > 1 ? 's' : ''} held this month`
  },

  install: {
    title: 'Put SELFCNTRL on your home screen',
    body: 'One tap from your days, your lock and the SOS. No app store, nothing to download.',
    bodyIos: 'Three taps in Safari, and it lives next to your other apps.',
    cta: 'Add to home screen',
    // Version BARRE (tableau de bord) : une ligne, donc trois mots maximum.
    barT: 'Keep it one tap away',
    barCta: 'Add',
    barHow: 'How',
    barClose: 'Hide',
    ios1: 'Tap Share, at the bottom of Safari',
    ios2: 'Choose “Add to Home Screen”',
    ios3: 'Tap Add. Done.',
    doneT: 'Installed.',
    doneS: 'Find SELFCNTRL on your home screen.'
  },

  sos: {
    breathK: 'Right now',
    breathT: 'Breathe first.',
    breathOn: 'In… and out.',
    breathP: 'One minute. Nothing else to do. The urge peaks and passes — it always does.',
    breathCta: 'Start the minute',
    skip: 'Skip',
    stepK: (i, n) => `Step ${i} of ${n}`,
    steps: [
      { t: 'Stand up. Leave the room.', p: 'Physically. Take the phone if you must, but change the walls.' },
      { t: 'Drink a glass of water.', p: 'Cold if you can. Slowly.' },
      { t: 'Write one line.', p: 'What you feel, in your own words. Nobody reads it.', write: true }
    ],
    notePh: 'Right now I…',
    next: 'Done — next',
    stepsDone: 'Done',
    peopleK: 'You are not alone',
    peopleT: 'Someone is awake.',
    peopleP: '#urge-sos on the Discord exists for this exact minute. Say it out loud.',
    discordCta: 'Open #urge-sos',
    backHome: 'Back to my space',
    slippedP: 'If it already happened:'
  },

  installs: {
    title: 'Installations · last 14 days',
    total: n => `${n} in total`,
    tooltip: n => `installation${n > 1 ? 's' : ''}`
  },

  protection: {
    title: 'My lock',
    emptyLead: 'Create your blocking profile to get started.',
    summary: (p, d, locked) =>
      `${p} profile${p > 1 ? 's' : ''} · ${d} device${d > 1 ? 's' : ''}` +
      (d ? ` · ${locked} locked` : ''),
    addProfile: 'Add a profile',
    nameLabel: 'Profile name',
    namePlaceholder: 'My devices',
    creating: 'Creating…',
    create: 'Create',
    emptyT: 'No profile yet',
    emptyS: 'A profile brings together a filtering level, curfews and your devices.',
    emptyCta: 'Create my profile',
    firstT: 'Lock your first device.',
    firstP: 'About ten minutes. Pick the device, follow the steps, and the filter can’t come off on impulse.',
    firstCta: 'Lock my first device',
    defaultName: 'Me',
    ghostT: 'Add a profile',
    ghostS: 'Another level, other devices.',
    abandon: name => `Abandon ${name}`,
    stateOn: 'Lock active',
    stateOff: 'Being set up',
    filtering: 'Filtering',
    strict: 'Strict',
    filteringHint: `Pornography, nudity and adult themes blocked at the root,
                    continuously, on every device in the profile.`,
    filteringNote: `There is no weaker level: a lock you can loosen is not a
                    lock.`,
    devices: 'Devices',
    noDevices: `No devices yet. They appear here on their own, once locked.`,
    deviceNote: `A device can't be removed in one click: the profile would stay
                 installed on it. It detaches by abandoning the profile, with
                 its cooling-off period.`,
    locked: 'Locked',
    pending: 'Pending',
    supervisedSuffix: ' · supervised',
    active: 'Active',
    inactive: 'Inactive',
    lastSeen: x => `seen ${x} ago`,
    ago: { now: 'just now', m: 'min', h: 'h', d: 'd' },
    dirty: 'Changes not applied',
    applying: 'Applying…',
    ctaPending: 'Scan the QR from the device you want to lock.',
    ctaDone: 'All devices are locked.',
    lock: 'Lock',
    // Compact card (redesign)
    filterStrict: 'Strict filtering',
    stProtected: 'Protected',
    stToLock: 'To lock',
    // Locked, but worth a look: the Guard reports itself off, or the device
    // has sent no DNS query for two weeks (profile removed by hand?).
    stCheck: 'To check',
    warnGuardOff: 'filtering stopped',
    stPartial: (n, tot) => `${n}/${tot} protected`,
    stNoDevice: 'No device',
    devicesLine: (n, locked) => `${n} device${n > 1 ? 's' : ''} · ${locked} locked`,
    settings: 'Settings',
    settingsHide: 'Hide settings',
    lockDevice: 'Lock a device',
    lockAnother: 'Lock another device',
    v2: {
      allOn: 'Everything is locked',
      partOn: 'Almost there',
      noneOn: 'Not protected yet',
      countLine: (n, tot) => `${n} of ${tot} device${tot > 1 ? 's' : ''} locked`,
      addS: 'iPhone, iPad, Mac or Windows · about 10 min',
      paceT: 'Quitting pace',
      paceS: 'Which days are locked, and how it tightens',
      moreT: 'Block more',
      moreS: 'Gambling, dating, social… and your own sites',
      cutsT: 'Internet cuts',
      cutsS: 'No internet at all during set hours'
    }
  },

  blockMore: {
    label: 'Block more',
    none: 'Nothing extra',
    count: n => `${n} blocked`,
    lead: 'On top of the enforced adult filter. It only ever tightens — removing a block asks for confirmation.',
    groups: {
      gambling: 'Gambling',
      dating: 'Dating',
      social: 'Social media',
      gaming: 'Gaming'
    },
    domainPlaceholder: 'Block a site (e.g. tinder.com)',
    add: 'Block',
    remove: 'Remove',
    confirmRemove: 'Remove this block? You are loosening your lock.'
  },

  weaningPicker: {
    label: 'Quitting rhythm',
    locked7: 'Locked 7 days/7',
    someDays: n => `${n} days/7`,
    pendingChange: left => <>Change scheduled in <b>{left}</b> — until then, your current days stay locked.</>,
    dayState: (full, locked) => `${full} — ${locked ? 'locked' : 'open'}`,
    adviceAll: 'Seven days a week. There is no evening left to negotiate with yourself.',
    adviceWeekend: `The weekend is the riskiest time: more free time, more
                    solitude. If you have to leave a day open, take a busy
                    weekday instead.`,
    adviceMin: `You're at the minimum. Aim for one more day within two weeks:
                that's the pace that holds over time.`,
    adviceOpen: n => `${n} day${n > 1 ? 's' : ''} still open.`,
    note: (floor, hours) => <>Floor reached: <b>{floor} days</b>, you don't go below it. Adding a day takes effect <b>immediately</b>; removing one waits <b>{hours} hours</b> — the urge to loosen up doesn't survive a night.</>,
    request: hours => `Request (${hours} h)`,
    apply: 'Apply',
    minDays: n => `Minimum ${n} days.`,
    hoursShort: (h, m) => `${h} h ${m}`,
    minutesShort: m => `${m} min`
  },

  schedules: {
    label: 'Cut the internet',
    add: '+ Add a cut',
    summaryNone: 'None',
    summaryCount: n => `${n} cut${n > 1 ? 's' : ''}`,
    tzNote: tz => `Times in your zone (${tz}) — change it in "My account".`,
    none: 'No cut scheduled. Relapses happen between 11pm and 7am.',
    noneNote: `Concentrate your sleep: the screen off at a fixed hour means
               falling asleep comes back and the dead hours disappear.`,
    everyDay: 'Every day',
    remove: 'Delete',
    formNote: (start, end) => `Cut the internet from ${start} to ${end} on every device in the profile.`,
    nameLabel: 'Name of the cut',
    namePlaceholder: 'Night',
    defaultName: 'Night',
    from: 'From',
    to: 'To',
    pickDay: 'Choose at least one day.',
    create: 'Create'
  },

  download: {
    mac: 'Download for Mac',
    macSub: 'Apple Silicon (M1–M4) · macOS 11+',
    oldMac: 'Have an older Intel Mac?',
    macIntel: 'Download — Intel version',
    macIntelSub: 'Intel processor · macOS 11+',
    win: 'Download for Windows',
    winSub: 'Windows 10 / 11 · 64-bit'
  },

  installModal: {
    v2: { copy: 'Tap to copy', copied: 'Copied ✓', forProfile: n => `Profile · ${n}` },
    title: name => `Lock a device · ${name}`,
    tab1: '1 · Supervise',
    tab2: '2 · Filter',
    superviseLead: <>On your computer, open the <b>SELFCNTRL Supervisor</b> app, plug in the iPhone, then enter this code. This is the step that makes the block impossible to remove.</>,
    generatingCode: 'Generating the code…',
    codeWarn: 'This code expires in 15 minutes and works only once.',
    tutorialLink: 'See the illustrated tutorial',
    filterLead: `Once the iPhone is supervised, scan this QR with its camera to
                 install the filtering.`,
    generatingLink: 'Generating the link…',
    qrAlt: 'Installation QR code',
    step1: 'iPhone camera → point it at the QR.',
    step2: <>Settings → General → <b>VPN & Device Management</b> → install.</>,
    openLink: 'Open the link',

    // Device-aware LOCK flow — one step at a time, confirm to advance.
    pick: 'Which device are you locking? The steps are different.',
    codeLabel: 'Pairing code',
    filterTitle: 'Install filtering',
    stepN: (i, n) => `Step ${i} of ${n}`,
    done: "I've done this",
    finish: 'Done',
    prev: 'Back',
    dlApp: 'Download SELFCNTRL (Windows)',
    dlItunes: 'Get iTunes (Windows, from apple.com)',
    helpTuto: 'Need help? Watch the video tutorial',
    ios: [
      { t: 'Open SELFCNTRL on your computer', p: 'Download SELFCNTRL for Windows or Mac, then open it. On Windows, iTunes is required (USB drivers). Have your Apple ID password ready.', dl: true, itunes: true },
      { t: 'Plug in the iPhone', p: 'Connect it to the PC by USB and tap “Trust” on the iPhone. In SELFCNTRL its name should appear. The app then walks you through turning Find My and Stolen Device Protection off.' },
      { t: 'Start supervision', p: 'In SELFCNTRL choose “Supervise an iPhone”, enter the code below, and start. The iPhone restarts once — nothing is erased.', code: true },
      { t: 'Put your protections back', p: 'Turn Find My and Stolen Device Protection back on. Supervision stays active no matter what.' },
      { t: 'Install filtering', p: 'Scan the code below with the iPhone camera, then Settings → General → VPN & Device Management → install.', qr: true }
    ],
    win: [
      { t: 'Download SELFCNTRL', p: 'Download SELFCNTRL below, unzip the folder, and open SELFCNTRL.exe on this PC.', dl: true },
      { t: 'Choose “Protect this computer”', p: 'Run the app — it asks for administrator rights — and choose “Protect this computer”.' },
      { t: 'Enter the code', p: 'Enter the code below when the app asks for it. There is no profile to scan on Windows.', code: true }
    ],
    mac: [
      { t: 'Download SELFCNTRL', p: 'Pick the build for your chip below (Apple Silicon or Intel), double-click the .tar.gz to extract it, then double-click SELFCNTRL.app. macOS confirms once (“downloaded from the internet”) → “Open”.', dl: true },
      { t: 'Choose “Protect this Mac”', p: 'In the app, choose “Protect this Mac”. It prepares the DNS filtering profile (Cloudflare), protected by your removal password.' },
      { t: 'Enter the code', p: 'Enter the code below when the app asks for it.', code: true },
      { t: 'Approve the profile', p: 'macOS opens the profile: go to System Settings → General → VPN & Device Management and approve “SELFCNTRL” to lock the filtering in.' }
    ],
    macLead: 'The supervisor app is Windows-only for now — supervising a Mac, or an iPhone from a Mac, is not available yet.',
    macLink: 'See the walkthrough and the Mac waitlist'
  },

  removalModal: {
    foeWon: 'It won this round.',
    foeWarn: 'Go through with it, and it wins.',
    releasedT: 'The lock can be removed',
    releasedLead: hours => `The ${hours} hours have passed. Here is the password to enter on the device to uninstall the profile, in Settings → General → VPN & Device Management.`,
    releasedWindows: `On a Windows PC, open the SELFCNTRL Retirer app and enter this same password.`,
    noPassword: `No password is stored on this account. Contact support to remove
                 the profile.`,
    releasedWarn: `Nothing forces you to use it. Plenty of people get this far
                   and close the page — it's the next round that counts.`,
    continueAnyway: "I'll keep going after all",
    deleteProfile: 'Delete the profile',
    requestedT: 'Request recorded',
    requestedLead: 'The password that uninstalls the lock will be given to you in:',
    requestedWarn: `This delay is exactly what you came for. The urge to stop
                    everything passes in a night; a considered decision holds
                    three days. You can close this page, the countdown keeps
                    running.`,
    cancelRequest: 'Cancel my request',
    formT: 'Abandon the challenge',
    formLead: hours => <>Removing this profile disables filtering on all its devices. Your day counter goes back to zero. The uninstall password will only be given to you <b>{hours} hours</b> after this request.</>,
    ack: `I acknowledge that I have lost the challenge I set myself, and that
          nobody else is to blame.`,
    typeLabel: name => `Type "${name}" to confirm`,
    startDelay: hours => `Start the ${hours} h delay`,
    keepGoing: "I'll keep going"
  },

  rank: {
    ladder: 'The ladder',
    fromDay1: 'From the first day',
    fromDays: n => `From ${n} days`,
    yourRank: 'Your rank',
    passed: 'Passed',
    recordT: 'Your record',
    currentStreak: 'Current streak',
    bestStreak: 'Best streak',
    relapses: 'Relapses declared',
    days: n => `${n} day${n > 1 ? 's' : ''}`,
    recordNote: <>A relapse resets the counter but <b>does not touch your record</b>. Without this button, the number on screen would just be your account age dressed up as a performance.</>,
    declareRelapse: 'I relapsed',
    confirmRelapse: `Reset your counter to zero?\n\nYour record is kept. It's the
only way to have a number that means something.`,
    leaderboard: 'Leaderboard',
    leave: 'Stop taking part',
    join: 'Take part',
    optOutT: "You're not taking part",
    optOutS: `Your record stays private. If you join the leaderboard, only the
              nickname you choose is visible — never your first name or your
              email.`,
    share: 'Share my record',
    aliasLabel: 'Your nickname',
    aliasPlaceholder: 'anonymous',
    defaultAlias: 'anonymous',
    aliasHint: 'Letters, digits, dot, dash and space. Your real name is never published.',
    demoNote: n => <><b>Leaderboard opening up.</b> {n} of the profiles shown are examples while the first users sign up.</>,
    you: ' · you',
    demo: ' · example',
    dayShort: 'd',
    more: 'More',
    seeAll: n => `See all ${n} →`,
    showLess: 'Show less'
  },

  goals: {
    title: 'Goals',
    emptyLead: 'The time you get back has to go somewhere.',
    lead: 'What you do with the hours you no longer lose.',
    tabGoals: 'My goals',
    tabTrack: 'Progress',
    // Onglet « Suivi » : évolution dans le temps + comparaison par domaine.
    track: {
      lead: 'Where the time is going, week after week.',
      streakNow: 'Best streak running',
      streakUnit: n => `${n} wk`,
      streakRecord: n => n > 0 ? `record ${n}` : 'no record yet',
      doneAll: 'Goals completed',
      doneAllSub: 'all time',
      cntrlAll: 'CNTRL earned',
      cntrlAllSub: 'all time',
      weeksTracked: 'Weeks tracked',
      weeksTrackedSub: 'closed cycles',
      weeklyHeldT: 'Goals held each week',
      weeklyHeldV: (held, total) => `${held} of ${total} kept`,
      cntrlWeekT: 'CNTRL earned each week',
      cntrlWeekV: n => `${n} in total`,
      evoEmpty: 'Your history fills in from your first “New week”. Keep at it — the chart starts next cycle.',
      weekTip: (date, held, total, coins) => `${date} — ${held}/${total} held · ${coins} CNTRL`,
      domainT: 'By area, this week',
      domainSub: (held, total, comp) => comp > 0
        ? `${held}/${total} held · ${comp} done in total`
        : `${held}/${total} held`,
      strong: 'strongest',
      weak: 'to work on',
      empty: 'Set a goal or two first — your progress shows up here.'
    },
    newWeek: 'New week',
    add: 'Add',
    weekDone: 'Week complete. Reset the counters and push the bar back up.',
    weekProgress: n => `goal${n > 1 ? 's' : ''} reached this week.`,
    formTitle: 'New goal',
    areaLabel: 'Area',
    suggestions: 'Suggestions',
    preview: 'Preview',
    goalLabel: 'The goal for the week',
    goalHint: 'Pick a suggestion above, or write your own.',
    howMany: 'How many',
    unit: 'Unit',
    unitPlaceholder: 'sessions, pages, min…',
    creating: 'Creating…',
    createGoal: 'Create the goal',
    emptyT: 'Pick your first goal.',
    emptyS: `Stopping frees up two to three hours a week. Decide now where they
             go, while you're clear-headed.`,
    emptyCta: 'Set a goal',
    reached: 'Reached.',
    left: (n, unit) => `${n} ${unit || ''} to go`.trim(),
    minusOne: 'Remove one',
    plusOne: 'Add one',
    plusN: n => `Add ${n}`,
    deleteGoal: title => `Delete ${title}`,
    confirmDelete: title => `Delete "${title}"?`,
    confirmReset: 'Reset every counter for the week?',

    // Reward (migration 016): completing a goal credits CNTRL.
    reward: n => `+${n} CNTRL`,
    rewardHint: n => `Finish it, +${n} CNTRL`,
    earnedTag: 'Earned',
    streakTitle: n => `${n} ${n > 1 ? 'weeks' : 'week'} in a row`,
    weekEarned: n => n > 0
      ? `You earned ${n} CNTRL this week.`
      : 'Finish a goal to earn your first CNTRL this week.',
    capNote: n => `Up to ${n} goals rewarded per week.`,
    toRewards: 'My rewards',
    autoResetNote: 'Counters reset on their own at the start of each week.',

    historyShow: 'See past weeks',
    historyHide: 'Hide',
    historyTitle: 'Past weeks',
    historyEmpty: 'Nothing yet. Your first week will be archived here.',
    histTotals: (done, cycles) => `${done} completed out of ${cycles} cycle${cycles > 1 ? 's' : ''}`,
    histCompleted: 'Done',
    histMissed: 'Missed',
    histWon: n => `+${n}`
  },

  domains: {
    sport: {
      label: 'Fitness',
      hint: 'Energy put back where it counts.',
      presets: [
        { title: 'Strength sessions', target: 3, unit: 'sessions' },
        { title: 'Kilometres run', target: 20, unit: 'km' },
        { title: 'Minutes of cardio', target: 90, unit: 'min' },
        { title: 'Push-ups', target: 300, unit: 'push-ups' }
      ]
    },
    addictions: {
      label: 'Addictions',
      hint: 'The rest of what decides in your place.',
      presets: [
        { title: 'Days without alcohol', target: 7, unit: 'days' },
        { title: 'Days without tobacco', target: 7, unit: 'days' },
        { title: 'Days without vaping or nicotine', target: 7, unit: 'days' },
        { title: 'Days without weed', target: 7, unit: 'days' },
        { title: 'Days without gambling', target: 7, unit: 'days' },
        { title: 'Days without energy drinks', target: 7, unit: 'days' }
      ]
    },
    lecture: {
      label: 'Reading',
      hint: 'The best replacement for scrolling.',
      presets: [
        { title: 'Pages read', target: 150, unit: 'pages' },
        { title: 'Chapters', target: 10, unit: 'chapters' },
        { title: 'Minutes reading in the evening', target: 210, unit: 'min' },
        { title: 'Book finished', target: 1, unit: 'book' }
      ]
    },
    personnel: {
      label: 'Personal',
      hint: 'Sleep, the people close to you, ordinary discipline.',
      presets: [
        { title: 'Nights in bed before 11pm', target: 5, unit: 'nights' },
        { title: 'Days without social media', target: 3, unit: 'days' },
        { title: 'Calls to people I care about', target: 2, unit: 'calls' },
        { title: 'Mornings up without snoozing', target: 6, unit: 'mornings' }
      ]
    },
    pro: {
      label: 'Professional',
      hint: 'Hours of real focus, not hours sitting down.',
      presets: [
        { title: 'Hours of focused work', target: 20, unit: 'h' },
        { title: 'Sessions without my phone', target: 10, unit: 'sessions' },
        { title: 'Courses or tutorials finished', target: 3, unit: 'modules' },
        { title: 'Applications sent', target: 5, unit: 'sent' }
      ]
    }
  },

  // The dashboard Rewards page. Named `cntrl`, not `rewards`: `rewards` is
  // already the landing section, and the dictionaries are merged.
  cntrl: {
    v2: {
      worth: usd => `Worth $${usd} in gift cards`,
      monthsChip: n => `month${n === 1 ? '' : 's'} held`,
      cardsChip: n => `card${n === 1 ? '' : 's'} ready`,
      nextK: 'Your next card',
      readyK: 'Ready to order',
      toNext: n => `${n} CNTRL to your next $5 card`,
      readyP: 'Pick a brand below and place the order.',
      earnT: 'How your pot grows',
      earnMonth: '+200 CNTRL',
      earnMonthS: 'Every month the lock holds',
      earnGoal: '+10 CNTRL',
      earnGoalS: 'Per goal completed, up to 5 a week',
      earnGoalCta: 'My goals',
      allBonus: 'You have every bonus',
      orderBtn: (n, b, cost) => `Order ${n} × ${b} · ${cost} CNTRL`,
      lockedBtn: n => `${n} CNTRL to go`,
      pickBrand: 'Pick your card',
      empty: 'Nothing yet. Your first 200 CNTRL land after your first month locked.'
    },
    title: 'Rewards',
    lead: 'Every month you hold, and every goal you finish, fills your pot.',
    balance: 'Your balance',
    unit: 'CNTRL',
    accruingOn: 'Your counter is running.',
    stateOn: 'Counter running',
    stateOff: 'Counter paused',
    progressT: 'Progress over time',
    progressNext: n => `+${n} CNTRL next`,
    progressHint: 'Keep the filter on — every month held moves this bar and adds a bonus.',
    giftcard: 'Gift card',
    paused: {
      no_plan: 'The counter starts on your first paid month.',
      trial: 'The trial does not count: the counter starts on your first paid month.',
      inactive: 'Counter paused: your subscription is not active.',
      filtering_off: 'Lock a device and your counter starts. 200 CNTRL every month it holds.',
      lockCta: 'Lock a device'
    },
    monthsHeld: n => `${n} month${n > 1 ? 's' : ''} held`,
    nextMilestone: (left, bonus) =>
      `${left} more month${left > 1 ? 's' : ''} to the +${bonus} CNTRL milestone.`,
    noMilestone: 'Every milestone reached.',
    cardsAvailable: n => n > 0
      ? `${n} × $5 card${n > 1 ? 's' : ''} available`
      : 'No card available yet',
    orderTitle: 'Order a gift card',
    orderLead: n => `1 card = ${n} CNTRL`,
    monthsHeldK: 'Months held',
    cardsK: 'Cards available',
    rateK: 'Rate',
    towardCard: (have, per) => `${have} / ${per} toward your next card`,
    monthsToGo: n => `${n} month${n > 1 ? 's' : ''} to go`,
    toGo: n => `${n} CNTRL to go`,
    seeAll: n => `See all ${n} →`,
    showLess: 'Show less',
    brand: 'Brand',
    brands: {
      amazon: 'Amazon',
      airbnb: 'Airbnb',
      starbucks: 'Starbucks',
      googleplay: 'Google Play',
      appstore: 'App Store'
    },
    cardsLabel: 'Number of cards',
    orderBtn: 'Place the order',
    ordering: 'Sending…',
    insufficient: 'Not enough balance for this order.',
    orderNote: 'Each card is sent by hand after review. Allow up to 48 h.',
    ordered: '🎉 Order recorded. Your card arrives by email shortly.',
    historyTitle: 'Your movements',
    reasons: {
      monthly: 'Month held',
      milestone: 'Milestone',
      redeem: 'Gift card',
      goal: 'Goal completed',
      adjust: 'Adjustment',
      promo: 'Bonus'
    },
    empty: 'No movement yet.',
    redemptionsTitle: 'Your orders',
    status: {
      pending: 'Pending',
      sent: 'Sent',
      refused: 'Refused'
    },
    card: (cards, brand) => `${cards} × ${brand}`,
    justCredited: n => `+${n} CNTRL just credited.`
  },

  subscription: {
    yours: 'Your subscription',
    v2: {
      noPlan: 'No plan yet',
      trialLine: n => `Trial running — ${n} day${n > 1 ? 's' : ''} left.`,
      activeLine: 'Renews automatically. Cancel anytime, your lock stays on.',
      inactiveLine: 'Your plan is not active: the lock and the CNTRL counter are paused.'
    },
    manage: 'Manage or cancel',
    manageMail: 'Cancel on WhatsApp',
    state: 'Status',
    plan: 'Plan',
    trialLeft: 'Trial remaining',
    days: n => `${n} day${n > 1 ? 's' : ''}`,
    labels: {
      trialing: 'Trial in progress',
      active: 'Active',
      past_due: 'Payment pending',
      canceled: 'Cancelled',
      none: 'No subscription'
    },
    planNames: { monthly: 'Monthly', yearly: 'Yearly', lifetime: 'Lifetime', none: '—' },
    noneNote: 'No payment recorded. Choose a plan below to get started.',
    change: 'Change plan',
    choose: 'Choose a plan',
    currentTag: 'Current plan',
    alreadyOn: 'Already active',
    coveredByLifetime: 'Included in your lifetime plan',
    redirecting: 'Redirecting…',
    trialCta: 'Subscribe',
    upgrade: 'Switch to yearly',
    payYear: 'Subscribe yearly',
    upgradeNote: `When you switch to yearly, remember to cancel your monthly
                  subscription from "Manage or cancel" — otherwise both run in
                  parallel.`,
    paymentNote: `Payment happens on a page hosted by our provider: no bank
                  details pass through SELFCNTRL. The label on your statement
                  stays discreet.`
  },

  account: {
    infoT: 'Information',
    firstName: 'First name',
    email: 'Email',
    plan: 'Plan',
    subscription: 'Subscription',
    memberSince: 'Member since',
    planNames: { monthly: 'Monthly', yearly: 'Yearly', lifetime: 'Lifetime', none: '—' },
    statuses: {
      trialing: 'Trial in progress',
      active: 'Active',
      past_due: 'Payment pending',
      canceled: 'Cancelled',
      none: 'None'
    },
    tzT: 'Time zone',
    tzLead: 'It aligns your internet cuts and weaning to your local time. Filled in automatically — fix it if needed.',
    tzLabel: 'Your time zone',
    tzSaved: 'Saved ✓',
    passwordT: 'Password',
    updated: 'Password updated.',
    current: 'Current password',
    next: 'New password',
    nextHint: 'Other connected devices will be logged out.',

    // Résiliation. Le texte dit franchement que c'est traité à la main et
    // sous quel délai : promettre l'instantané et mettre douze heures est
    // exactement ce qui envoie quelqu'un chez sa banque.
    cancelT: 'Cancel my subscription',
    cancelP: `Message us on WhatsApp: we stop the billing and confirm it in
              the conversation — under 24 hours.`,
    cancelBtn: 'Cancel on WhatsApp',
    // Pré-rempli dans WhatsApp, avec l'e-mail du compte pour retrouver
    // l'abonnement dans Stripe sans aller-retour.
    cancelMsg: email => `Hi, I'd like to cancel my SELFCNTRL subscription.${email ? ` Account: ${email}` : ''}`,
    // Le point qui évite le pire malentendu possible sur ce produit.
    cancelKeep: `Your lock stays on. Cancelling the subscription does not
                 unlock your devices — that goes through the 72-hour removal
                 delay, from the lock page.`
  },

  tutorial: {
    v2: {
      title: 'Set up your lock',
      sub: 'Download, follow along, lock it. About ten minutes, once per device.',
      deviceL: 'Device',
      fromL: 'Locking it from',
      from: { windows: 'a Windows PC', mac: 'a Mac', none: 'no computer' },
      winGetP: 'A small app that installs the filter and the service that keeps it on. Download it on this PC.',
      winWarnT: 'Windows shows a warning?',
      macWarnT: 'macOS asks for a confirmation?',
      listT: 'Step by step',
      screenOf: (i, n) => `${i} / ${n}`,
      wzGetK: 'Before you start',
      wzVideo: 'Watch this on video',
      wzStart: 'Got it, let’s go →',
      wzLast: 'Done, last check →',
      phoneT: 'You are on your phone',
      phoneP: 'This setup runs on the computer itself. Send yourself this page and open it there.',
      phonePi: 'The iPhone gets locked from a computer, with a cable. Send yourself this page and open it there.',
      send: 'Send to my computer',
      copied: 'Link copied ✓',
      nextStep: 'Done, next step →',
      undoShort: 'Not done yet',
      progress: (d, n) => `${d} / ${n} done`,
      markDone: 'Done',
      undo: 'Mark as not done',
      reset: 'Start over',
      allDone: 'All steps ticked. Check that the device shows "Locked" in My lock.',
      lockT: 'Check it is locked',
      lockP: 'Open My lock: the device shows "Locked". Your day counter starts at that moment.',
      helpT: 'Stuck on a step?',
      helpP: 'Message us on WhatsApp. We answer in under an hour, 9am to 11pm.',
      winGetT: 'Download SELFCNTRL for Windows'
    },
    title: 'Tutorial',
    subtitle: 'The full setup, on video. Count on about ten minutes.',
    writtenT: 'Step by step',
    flow: ['Pick the device', 'Watch the video', 'Lock it'],
    flowMin: '≈ 10 min',
    nextStepBtn: 'Next step',
    gotIt: 'Got it',
    lead: `Three parts: create your profile, lock the iPhone from your
           computer, then set your pace. Only the second one needs a cable —
           and it only happens once. Scroll each part step by step; click a
           screenshot to enlarge it.`,
    step: n => `Step ${n}`,
    stepOf: (i, n) => `Step ${i} of ${n}`,
    prevStep: 'Previous step',
    nextStep: 'Next step',
    stepsOf: title => `Steps — ${title}`,
    zoom: caption => `Enlarge: ${caption}`,
    enlarge: 'Tap to enlarge',

    // Téléchargement de l'app superviseur, en tête du tutoriel : c'est la
    // toute première chose à faire, et jusqu'ici rien sur le site ne disait
    // où la prendre.
    getT: 'Get the Supervisor app',
    getP: `Part 2 runs from your computer, not your phone. Download it now — you
           will need it, plus a cable, in about three minutes.`,
    getWin: 'Download for Windows',
    getWinSub: 'Installer · 85 MB · Windows 10 and 11, 64-bit',
    getPortable: 'Portable version (no install)',
    getMacArm: 'Download — Apple Silicon (M1–M4)',
    getMacIntel: 'Download — Intel Mac',
    getMacArmSub: 'Apple Silicon · 44 MB · macOS 11 and later',
    getMacIntelSub: 'Intel processor · 46 MB · macOS 11 and later',
    macChipHint: 'Which one? Apple menu  ▸ About This Mac: “Chip” = Apple Silicon, “Processor” = Intel.',
    macGetT: 'Download SELFCNTRL for Mac',
    macGetP: `One app for everything: protect this Mac (Cloudflare DNS filtering)
              or supervise an iPhone plugged in over USB. Grab the build that
              matches your chip.`,
    macGetWarn: `Signed by Apple (Developer ID) and notarized: double-click
                 SELFCNTRL.app to open it. macOS asks for one confirmation the
                 first time (“downloaded from the internet”) → “Open”.`,
    macSteps: [
      { t: 'Download and open SELFCNTRL', p: 'Grab the build for your chip above, double-click the .tar.gz to extract it, then double-click SELFCNTRL.app. macOS confirms once (“downloaded from the internet”) → “Open”.' },
      { t: 'Choose your action', p: '“Protect this Mac” installs Cloudflare DNS filtering on the Mac itself. “Supervise iPhone” puts an iPhone connected over USB into supervised mode — it restarts once, and Find My must be off first.' },
      { t: 'Enter your pairing code', p: 'Get it from your SELFCNTRL space ▸ Devices (format ABCD-2345) and type it when the app asks.' },
      { t: 'Approve the profile', p: 'For the Mac, macOS opens the profile: System Settings ▸ General ▸ VPN & Device Management ▸ approve “SELFCNTRL”. Removal is protected by your password.' }
    ],
    // SmartScreen crie « Éditeur inconnu » faute de certificat de signature.
    // Le dire ici évite qu'on prenne le produit pour un virus et qu'on
    // abandonne à la première alerte — c'est un abandon très courant.
    getWarn: `The app isn’t code-signed yet, so Windows may show one of two things.
              A BLUE “unknown publisher” screen: click “More info”, then “Run anyway”.
              If instead a window says “Smart App Control blocked an app…” with no button
              to run it, that’s Smart App Control: on YOUR own PC only, turn it off in
              Windows Security → App & browser control → Smart App Control → Off, then
              relaunch the file.`,
    troubleT: 'When it gets stuck',
    troubleLead: 'The blockers people report to us.',
    endP: `Stuck on a step? Write to us, we answer in under an hour between 9am
           and 11pm.`,
    endLock: 'Go to my lock',
    endWrite: 'Write to us',
    parts: [
      {
        id: 'preparer',
        n: '01',
        title: 'Prepare',
        lead: 'Two minutes on the site. Nothing touches your phone yet.',
        steps: [
          {
            t: 'Find your way around',
            p: `This is the dashboard. As long as no device is locked, the
                banner at the top tells you so plainly — it will never
                pretend you're protected when you're not.`,
            shot: ['site-2-espace.png', 'The overview', 'wide']
          },
          {
            t: 'Create your profile',
            p: `Go to "My lock" and click "Add a profile". Give it whatever
                name you want — most people only ever have one.`,
            shot: ['site-3-creer-profil.png', 'The creation form', 'wide'],
            note: `A profile brings together a filtering level, cut-off
                   schedules and the devices that carry it.`
          },
          {
            t: 'What a profile contains',
            p: `Filtering is strict and can't be adjusted: pornography,
                nudity and adult themes blocked continuously. Below it come
                the quitting rhythm, the devices, and the internet cuts.`,
            shot: ['site-4-profil.png', 'A complete profile card', 'tall']
          }
        ]
      },
      {
        id: 'verrouiller',
        n: '02',
        title: 'Lock the iPhone',
        lead: `Ten minutes, once, with the iPhone plugged into your computer.
               This is the step that makes the block impossible to tear off.`,
        steps: [
          {
            t: 'Open the supervisor app',
            p: `Download and launch SELFCNTRL Supervisor on your PC. Before
                you start, check the three prerequisites it lists: the cable,
                iTunes open (the version from apple.com, not the Microsoft
                Store one) and your Apple account password.`,
            shot: ['app-01-intro.png', 'The app home screen', 'tall']
          },
          {
            t: 'Turn off Stolen Device Protection',
            p: `On the iPhone: Settings → Face ID & Passcode → Stolen Device
                Protection → turn it off. If the iPhone has no passcode, skip
                this step.`,
            shot: ['app-02-protection.png', 'Step 2 of 8', 'tall'],
            note: `It's temporary. You put everything back five steps later,
                   and supervised mode stays active no matter what.`
          },
          {
            t: 'Turn off "Find My"',
            p: `Settings → your name → Find My → Find My iPhone → turn it off.
                Your Apple password will be requested. Without this, Apple
                flatly refuses supervision.`,
            shot: ['app-03-findmy.png', 'Step 3 of 8', 'tall'],
            note: `Temporary, like the previous step. Once supervision is done
                   you turn Find My back on (step 6) and it works exactly as
                   before — same account, same devices, nothing lost.`
          },
          {
            t: 'Plug in the iPhone',
            p: `Connect it by USB and tap "Trust" on its screen. The app
                detects it on its own: when its name and iOS version appear,
                the button lights up.`,
            shot: ['app-04-plug.png', 'The device is detected', 'tall'],
            note: `Nothing happening? Use the original cable (some cables only
                   charge), and check that iTunes is actually open.`
          },
          {
            t: 'Start supervision',
            p: `Click "Supervise now" and don't unplug. The iPhone restarts
                once during the operation: that's normal, no data is erased.`,
            shot: ['app-05-supervise.png', 'Step 5 of 8', 'tall'],
            warn: `Back it up beforehand if the device holds things you care
                   about. We erase nothing, but a backup costs nothing either.`
          },
          {
            t: 'Put the protections back',
            p: `Turn Find My iPhone and Stolen Device Protection back on. The
                iPhone may ask you to sign in to your Apple account again:
                sign in normally.`,
            shot: ['app-07-protections.png', 'Step 6 of 8', 'tall']
          },
          {
            t: 'Get the code from the site',
            p: `Go back to "My lock" and click "Lock". An eight-character code
                appears, valid for fifteen minutes and for one use only.`,
            shot: ['site-7-code.png', 'The pairing code', 'tall']
          },
          {
            t: 'Attach the device',
            p: `Copy the code into the app, then confirm. The iPhone is now
                linked to your account.`,
            shot: ['app-08-pair.png', 'Step 7 of 8', 'tall'],
            note: `Code expired? Close the window on the site and click "Lock"
                   again: a new code is generated.`
          },
          {
            t: 'Install the filtering',
            p: `In the same window on the site, switch to the "2 · Filter" tab
                and point the iPhone camera at the QR code. The profile
                downloads, then installs in Settings → General → VPN & Device
                Management.`,
            shot: ['site-8-qr.png', 'The installation QR code', 'tall'],
            note: `iOS will say "Not verified": that's normal, the profile
                   isn't signed by Apple. Install it anyway.`
          },
          {
            t: "It's locked",
            p: `The app announces that the device is attached and supervised.
                You can close it: it's of no further use until you add another
                device.`,
            shot: ['app-09-done.png', 'Step 8 of 8', 'tall']
          },
          {
            t: 'Check it on the site',
            p: `The device appears on its own in the profile's list, marked
                "Locked". Nothing to type by hand: a hand-written line would
                show "Locked" without the phone actually being locked.`,
            shot: ['site-4b-appareils.png', 'The device in your profile', 'wide']
          }
        ]
      },
      {
        id: 'vivre',
        n: '03',
        title: 'Set it up, then live with it',
        lead: 'The lock is set. The rest is your pace.',
        steps: [
          {
            t: 'Choose your quitting rhythm',
            p: `Either you cut it dead, seven days a week. Or you climb in
                steps, locking four days a week to begin with. Either way you
                can always add more — never remove below the step you've
                reached.`,
            shot: ['site-5-sevrage.png', 'The day picker', 'wide'],
            note: `A reduction doesn't apply immediately: it waits twenty-four
                   hours. So one weak evening isn't enough to undo what you
                   decided in the morning.`
          },
          {
            t: 'Cut the internet at night',
            p: `Add a time window — 11pm to 7am, for example — and the internet
                stops entirely on the profile's devices during that slot. It's
                the single most effective measure: almost all relapses happen
                between midnight and four.`,
            shot: ['site-6-couvre-feu.png', 'A scheduled cut', 'wide']
          },
          {
            t: 'Follow your rank',
            p: `The counter starts from the day you began, and eight milestones
                mark the road, from Fallen to Chosen. If you relapse, the
                "I relapsed" button resets the counter — without that button,
                the number would mean nothing.`,
            shot: ['site-9-rang.png', 'The rank ladder', 'wide']
          },
          {
            t: 'Set yourself goals',
            p: `Five areas: fitness, addictions, reading, personal,
                professional. The time you no longer lose has to go somewhere
                — that's what this page is for.`,
            shot: ['site-10-objectifs.png', 'The goals page', 'wide']
          },
          {
            t: 'If you want to remove everything',
            p: `It's possible, but not instantly. From the profile card, the
                removal request opens a seventy-two-hour cooling-off period.
                At the end of that delay, and only then, the uninstall
                password appears.`,
            note: `Support cannot shorten this delay. It isn't stubbornness: if
                   a message were enough to cancel it, the product would have
                   no value at all.`
          }
        ]
      }
    ],
    faq: [
      ['"No iPhone plugged in"',
       `iTunes has to be installed from apple.com and open — the Microsoft
        Store version doesn't install the USB driver. Use the original cable
        and remember to tap "Trust" on the phone's screen.`],
      ['"Supervision failed"',
       `Nine times out of ten, Find My iPhone was left on, or Stolen Device
        Protection wasn't turned off. Redo the two preparation steps, then try
        again.`],
      ["The pairing code won't go through",
       `It expires after fifteen minutes and works only once. Click "Lock"
        again from the site to get a new one.`],
      ['iOS says the profile is not verified',
       `Normal: it isn't signed by Apple. It changes nothing about how it
        works.`],
      ['Can I lock several devices?',
       `Yes. Repeat part 2 for each one; they all sit under the same profile
        and share its settings.`],
      ['I uninstalled the profile without the password',
       `Then write to us: that's exactly the $100 bet, and it's described in
        detail in the refund policy.`]
    ],

    // La FAQ du chemin Windows : filtrer un PC bute sur SmartScreen et Smart
    // App Control (app non signée), pas sur la supervision iOS.
    faqWin: [
      ['"Windows protected your PC" (blue box)',
       `That's SmartScreen flagging an unsigned app — not a virus. Click
        "More info", then "Run anyway". It's safe; it just isn't
        code-signed yet.`],
      ['"Smart App Control blocked an application" — with no Run button',
       `A stricter block, and only on some PCs. On YOUR machine, open Windows
        Security → App & browser control → Smart App Control → Off, then launch
        the file again.`],
      ["It won't open from the download",
       `Don't run it straight from the .zip. Right-click the file, choose
        "Extract all", then open SELFCNTRL.exe from the extracted folder.`],
      ["The pairing code won't go through",
       `It expires after fifteen minutes and works only once. Click "Lock"
        again on the site to get a fresh one.`],
      ['Does quitting the app lift the filter?',
       `No. The filter runs as a background service that relaunches on every
        boot, so closing the window or reinstalling the app doesn't unblock
        anything.`],
      ['Can I lock several computers?',
       `Yes. Run the app on each one and paste the same profile's code; they
        all share its settings.`]
    ]
  }
}

export default app
